import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, CalendarCheck } from "lucide-react";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture, TAN_STANDING_SEAM_PINE_RIDGE } from "@/components/site/images";

const CRUMBS = [
  { label: "Service Areas", href: "/service-areas" },
  { label: "Pine Ridge, FL", href: "/roofing-company-pine-ridge-fl" },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "What is the difference between standing seam and exposed-fastener metal roofing?",
    a: "On an exposed-fastener roof, screws pass through the face of the panel into the structure, so the fasteners and their washers are exposed to weather and need periodic inspection as the roof ages. On a standing-seam roof, the primary panel fasteners are concealed beneath the seams. Standing seam costs more; exposed fastener is a proven, more affordable metal system. Destination Roofing installs both.",
  },
  {
    q: "Is metal roofing a good choice in Florida?",
    a: "It can be. Metal handles sun, heavy rain and wind well and generally has a longer service life than asphalt shingles, which is why homeowners planning to stay in a house often consider it. It costs more up front, so the decision usually comes down to budget and how long you plan to own the home.",
  },
  {
    q: "Can metal be installed over an existing roof?",
    a: "Sometimes, but it depends on the existing assembly, the condition of the decking and the requirements of the panel system. We evaluate the actual roof before making that call rather than assuming it either way — a tear-off is often the more reliable path because it lets us see and correct the decking.",
  },
  {
    q: "How long does a roof replacement take?",
    a: "It depends on the size of the roof, the complexity of the detail work, the system being installed and the weather. Larger roofs and metal systems generally take longer than a straightforward shingle roof. We give you a realistic timeline with your written estimate rather than a generic number.",
  },
  {
    q: "Do you repair metal roofs, or only replace them?",
    a: "We repair them as well. Loose or backed-out fasteners, failed sealant at penetrations, damaged panels and flashing problems can often be corrected without replacing the roof, provided the panels themselves are still in good condition.",
  },
  {
    q: "Do you still install shingle roofs here?",
    a: "Yes. Architectural asphalt shingles are the most common residential roofing system in Florida, and Destination Roofing primarily installs Atlas shingle systems. Plenty of homeowners replace shingle with shingle, and that is a perfectly good decision.",
  },
  {
    q: "Is the roof inspection really free?",
    a: `Yes — the inspection and the written estimate are free, and owner Deston Judy generally performs inspections personally. Destination Roofing Inc. is licensed and insured, License #${BUSINESS.licenseNumber}.`,
  },
];

function TrustRow() {
  const items = [
    "Free Roof Inspection & Estimate",
    "Standing-seam and exposed-fastener metal",
    "Owner-performed inspections",
    "Family-owned, licensed and insured",
    `License #${BUSINESS.licenseNumber}`,
    "Repair and full replacement options",
  ];
  return (
    <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2">
            <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const Route = createFileRoute("/roofing-company-pine-ridge-fl")({
  head: () => ({
    meta: [
      { title: "Roofing Company in Pine Ridge, FL | Destination Roofing" },
      {
        name: "description",
        content:
          "Pine Ridge, FL roofing company for standing seam metal roofing, shingle replacement and roof repair. Family-owned, licensed & insured. Free inspection: (352) 901-3094.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Roofing Company in Pine Ridge, FL | Destination Roofing" },
      {
        property: "og:description",
        content:
          "Standing-seam and exposed-fastener metal roofing, shingle replacement and roof repair for Pine Ridge homeowners. Free roof inspection and estimate.",
      },
      { property: "og:url", content: canonical("/roofing-company-pine-ridge-fl") },
      { property: "og:type", content: "website" },
      { property: "og:image", content: TAN_STANDING_SEAM_PINE_RIDGE.fallback },
      { name: "twitter:image", content: TAN_STANDING_SEAM_PINE_RIDGE.fallback },
    ],
    links: [{ rel: "canonical", href: canonical("/roofing-company-pine-ridge-fl") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify([
          breadcrumbListJsonLd(CRUMBS),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Roofing services in Pine Ridge, FL",
            serviceType: "Roofing Contractor",
            description:
              "Metal roofing, standing seam metal roofing, shingle roof replacement, roof repair and free roof inspections for homeowners in Pine Ridge, Florida.",
            url: canonical("/roofing-company-pine-ridge-fl"),
            provider: { "@id": ROOFING_CONTRACTOR_ID },
            areaServed: { "@type": "Place", name: "Pine Ridge, Florida" },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />
      <PageHero
        eyebrow="Local Roofing"
        title="Roofing Company in Pine Ridge, FL"
        subtitle="Destination Roofing Inc. installs standing-seam and exposed-fastener metal roofing, Atlas shingle systems and roof repairs across Citrus County. Free Roof Inspection & Estimate."
      />
      <ServicePageBody title="Your Free Inspection" service="Roofing in Pine Ridge, FL">
        <p>
          Destination Roofing Inc. is a family-owned, licensed and insured Citrus County roofing contractor, and Pine Ridge is part of our regular working area. We are a service-area business — we come to the property, the owner looks at the roof, and you get photographs and a written estimate before anything is scheduled.
        </p>
        <TrustRow />

        <h2>A Standing-Seam Metal Roof We Installed Here</h2>
        <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
          <ResponsivePicture
            image={TAN_STANDING_SEAM_PINE_RIDGE}
            alt="Tan standing-seam metal roof installed on a new-construction home in Pine Ridge, Florida"
            sizes="(min-width: 1024px) 66vw, 100vw"
            imgClassName="w-full h-auto"
          />
          <figcaption className="p-3 text-sm text-muted-foreground">
            New-construction standing-seam metal roof installed by Destination Roofing in Pine Ridge, FL.{" "}
            <Link to="/projects/standing-seam-metal-roof-pine-ridge-fl">View the full project</Link>.
          </figcaption>
        </figure>
        <p>
          This is a real project from our own portfolio rather than a stock photo. Standing seam uses concealed primary fasteners beneath the seams, so the panel screws are not sitting out in the weather the way they are on an exposed-fastener roof. It is a premium system, and it is a common request on larger residential roofs where the homeowner wants a long service life and a clean panel line. You can see more of this work on our <Link to="/metal-roofing">metal roofing</Link> page.
        </p>

        <h2>Metal Roofing Options</h2>
        <p>
          Destination Roofing installs both metal systems, and they suit different budgets and different roofs:
        </p>
        <h3>Standing-seam metal roofing</h3>
        <p>
          The primary panel fasteners are concealed under the seams. Available configurations may include snap-lock panels with a screw strip, snap-lock panels installed with clips, and mechanically seamed panels. Our most common configuration is snap lock with a concealed screw strip, depending on the roof design and the manufacturer's requirements.
        </p>
        <h3>Exposed-fastener metal roofing</h3>
        <p>
          Screws pass through the panel face into the structure. It is a proven and more affordable way into a metal roof. The trade-off is that fasteners and sealants are exposed and need periodic inspection as the roof ages — screws driven too loose or over-tightened cause problems years later, which is why fastening discipline matters more on this system than almost anything else.
        </p>
        <p>
          Which one fits your house depends on the roof structure, the slope, the number of penetrations and your budget. We will show you both rather than steering you.
        </p>

        <h2>Larger Residential Roofs</h2>
        <p>
          Bigger roofs are not just more of the same. More surface area means more panel runs to keep straight, more material staged on site, and more places where two roof planes meet. On a metal roof in particular, panel layout has to be planned before the first panel goes on, because a run that drifts out of square shows from the driveway.
        </p>
        <p>
          Roofs with several sections can also age unevenly — a lower porch or lanai roof may be failing while the main roof has years left. We price the work the roof actually needs, and we will tell you when a section can wait.
        </p>

        <h2>Shingle Replacement</h2>
        <p>
          Metal is not the right answer for every homeowner, and we install a lot of shingle roofs. Destination Roofing primarily installs Atlas shingle systems, and a well-installed architectural shingle roof remains the most common residential roofing choice in Florida.
        </p>
        <p>
          On a replacement, the details under the shingles decide how long it lasts: the condition of the decking once the old roof is off, the underlayment, valley detailing, flashing at walls and penetrations, and correct fastening. Our <Link to="/shingle-roofing">shingle roofing</Link> page walks through those standards, and <Link to="/roof-replacement">roof replacement</Link> covers the full process.
        </p>

        <h2>Roof Repair</h2>
        <p>
          Not every problem calls for a new roof. Common repairs include pipe-boot failures, valley and flashing issues, wind-lifted or missing shingles, damaged metal panels, and backed-out or failed fasteners on older metal roofs. If the roof is otherwise in good shape and the source can be isolated, a targeted <Link to="/roof-repair">roof repair</Link> is usually the better use of your money.
        </p>
        <p>
          Replacement tends to make more sense when wear is widespread, when leaks keep returning in new places, or when flashings and details are failing across the roof rather than in one spot.
        </p>

        <h2>Florida Weather and Roof Performance</h2>
        <p>
          Sun and UV age roofing materials from above, heavy rain tests every flashing and valley, humidity and poor attic airflow work on the assembly from underneath, and storm-season wind finds anything that is not fastened correctly. Properties with mature tree cover collect debris in valleys and gutters, and debris that stays wet keeps the roof wet.
        </p>
        <p>
          The practical takeaway is the same for metal and shingle roofs: keep the valleys and gutters clear, look at the roof after a serious storm, and get anything questionable checked before it reaches the ceiling. If a storm has already hit, see <Link to="/storm-damage-roof-repair">storm-damage roof repair</Link>.
        </p>

        <h2>Wind Mitigation</h2>
        <p>
          A roof replacement can be the right moment to improve how the roof structure is fastened, including third-nail wind mitigation. Eligible improvements may help a homeowner qualify for insurance credits, though the insurance company determines the actual outcome. Ask about it during your inspection — details are on our <Link to="/third-nail-wind-mitigation">third-nail wind mitigation</Link> page.
        </p>

        <h2>Who You Are Actually Hiring</h2>
        <p>
          Destination Roofing is family-owned. Owner Deston Judy generally performs roof inspections personally and stays involved with the projects, so the person who assessed your roof is the person you can ask about it later. We are licensed and insured — Florida Roofing License #{BUSINESS.licenseNumber} — and we give honest answers about whether a repair will hold.
        </p>
        <p>
          You can see the rest of the communities we cover on our <Link to="/service-areas">service areas</Link> page.
        </p>

        <h2>Frequently Asked Questions</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((f) => (
            <details key={f.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{f.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>

        <h2 id="request-inspection">Schedule Your Free Roof Inspection</h2>
        <p>
          Whether you are pricing a standing-seam metal roof, comparing it against a shingle replacement, or chasing down a single leak, a free <Link to="/roof-inspection">roof inspection</Link> is the place to start. We walk the roof, take pictures, explain what we find in plain language, and leave you with a written estimate.
        </p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 my-4">
          <Link to="/contact" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Inspection
          </Link>
          <a
            href={BUSINESS.phoneHref}
            className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors"
          >
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </ServicePageBody>
    </>
  );
}
